import {
  loginBookingNumber,
  loginCorporate,
  resetPassword,
  sendCode,
  confirmCode,
  getLoginOptions,
  loginToken,
} from "./services";

import router from "@/router";
import persistData from "@/lib/persist-data";

const getDefaultState = () => {
  return {
    userData: null,
    token: null,
    type: "client",
    loginOptions: [],
    confirmId: "",
  };
};

const state = getDefaultState();

// internal consultants open bookings via a URL ending in /int - navigator.onLine
// can wrongly report offline on office PCs (e.g. when Windows' connectivity probe
// is blocked by the proxy), so never block them on it
const isInternalSource = (source) => {
  const routeSource = router.currentRoute.params.source;
  return [source, routeSource].some(
    (s) => typeof s === "string" && s.toLowerCase() === "int"
  );
};

const getters = {
  isAuthenticated: (state) => !!state.userData,
  isAuthorisedWithToken: (state) => !!state.token,
  getUserData: (state) => state.userData,
  getUserType: (state) => state.type,
  getLoginOptions: (state) => state.loginOptions,
  getConfirmId: (state) => state.confirmId,
};

const actions = {
  // will set authorized data to state and triger sequence of actions to run on login
  async authorizedAction({ dispatch, commit }, data) {
    const loadBookingData = { ...data.userData };
    if (data.openedBookingNumber) {
      loadBookingData.bookno = data.openedBookingNumber;
    }
    if (data.noBookingInit) {
      commit("loginBookingNumberSuccess", data.userData);
      return true;
    }
    // only mark the user as authenticated once the booking has actually loaded -
    // initAppAction itself commits loginBookingNumberSuccess once that happens,
    // otherwise isAuthenticated flips true before we know the booking is unavailable
    return await dispatch("initAppAction", loadBookingData, { root: true });
  },
  // login with booking number - called from the booking component on created
  async loginBookingNumberAction(
    { dispatch, rootState },
    { username, bookingNumber, rememberMe, source }
  ) {
    dispatch("spinner/updateLoadingAction", true, { root: true });

    const res = await loginBookingNumber(username, bookingNumber, source);
    if (!isInternalSource(source) && !navigator.onLine) {
      dispatch("spinner/updateLoadingAction", false, { root: true });
      dispatch(
        "flashMessage/set",
        {
          type: "error",
          message: "An internet connection could not be detected.",
        },
        { root: true }
      );
      return;
    }
    if (res && res.booking) {
      if (rememberMe) {
        persistData.setData("viewtrailData", res.booking);
      }
      persistData.setData("openedBookingNumber", bookingNumber);
      const resAuth = await dispatch("authorizedAction", {
        userData: res.booking,
      });
      dispatch("spinner/updateLoadingAction", false, { root: true });
      if (!resAuth) {
        // a more specific message (e.g. "booking is being processed") may already
        // have been set while loading the booking - don't stomp on it with the
        // generic mismatch message
        if (!rootState.flashMessage.message) {
          return dispatch(
            "flashMessage/set",
            {
              type: "error",
              message:
                "The Surname and/or Trailfinders Reference entered do not match, please check and try  again.",
            },
            { root: true }
          );
        }
        return false;
      }
      return !!resAuth;
    } else {
      dispatch("spinner/updateLoadingAction", false, { root: true });
      dispatch(
        "flashMessage/set",
        {
          type: "error",
          message:
            "The Surname and/or Trailfinders Reference entered do not match, please check and try  again.",
        },
        { root: true }
      );
    }
  },
  // corporate login
  async loginCorporateAction(
    { dispatch, commit },
    { clientref, email, password, corporate, rememberMe }
  ) {
    dispatch("spinner/updateLoadingAction", true, { root: true });
    const tokenRes = await loginToken(email, password, corporate);
    if (tokenRes && tokenRes.token) {
      const res = await loginCorporate(clientref, email, tokenRes.token);
      if (res && res.booking) {
        commit("loginBookingNumberSuccess", res.booking);
        commit("loginTokenSuccess", tokenRes.token);
        commit("setUserType", "corporate");

        if (rememberMe) {
          persistData.setData("viewtrailData", res.booking);
          persistData.setData("token", tokenRes.token);
        }
        dispatch("spinner/updateLoadingAction", false, { root: true });
        return true;
      } else {
        dispatch("spinner/updateLoadingAction", false, { root: true });
        dispatch(
          "flashMessage/set",
          {
            type: "error",
            message:
              "Corporate portal not found. Please check Client Number, Email and Password and try again.",
          },
          { root: true }
        );
      }
    } else {
      dispatch("spinner/updateLoadingAction", false, { root: true });
      dispatch(
        "flashMessage/set",
        {
          type: "error",
          message:
            "Corporate portal not found. Please check Client Number, Email and Password and try again.",
        },
        { root: true }
      );
    }
  },
  async loginBookingNumberOnStartAction({ dispatch }, data) {
    // check if we were previously remembered
    const rememberMe = !!persistData.getData("viewtrailData");
    if (data && data.bookingNumber && data.username) {
      dispatch("logoutAction", true);
      const authData = { ...data, rememberMe, noRedirect: true };
      return await dispatch("loginBookingNumberAction", authData);
    } else {
      return true;
    }
  },
  // send code to chosen method
  async loginSendCodeAction(
    { dispatch, commit },
    { bookingNumber, codeDestination }
  ) {
    dispatch("spinner/updateLoadingAction", true, { root: true });
    if (!isInternalSource() && !navigator.onLine) {
      dispatch("spinner/updateLoadingAction", false, { root: true });
      dispatch(
        "flashMessage/set",
        {
          type: "error",
          message: "An internet connection could not be detected at this time.",
        },
        { root: true }
      );
      return;
    }
    const res = await sendCode(bookingNumber, codeDestination);
    if (res && res.id) {
      commit("confirmIdReceived", res.id);
      dispatch("spinner/updateLoadingAction", false, { root: true });
    } else {
      commit("confirmIdReceived", "");
      dispatch("spinner/updateLoadingAction", false, { root: true });
      dispatch(
        "flashMessage/set",
        { type: "error", message: "Failed to send Code." },
        { root: true }
      );
    }
  },
  async resetConfirmId({ dispatch, commit }) {
    commit("confirmIdReceived", "");
  },
  // Narrow token clear (unlike resetStateAction, which also wipes
  // userData/loginOptions/etc) - used to force a genuinely fresh OTP
  // confirm cycle for flows that need to know exactly who is verifying
  // right now (cancellation, Communications access), rather than silently
  // accepting a token obtained earlier for an unrelated purpose.
  clearTokenAction({ commit }) {
    commit("clearToken");
  },
  async loginConfirmCodeAction(
    { dispatch, commit },
    { code, confirmId, destination }
  ) {
    dispatch("spinner/updateLoadingAction", true, { root: true });
    if (!isInternalSource() && !navigator.onLine) {
      dispatch("spinner/updateLoadingAction", false, { root: true });
      dispatch(
        "flashMessage/set",
        {
          type: "error",
          message: "An internet connection could not be detected at this time.",
        },
        { root: true }
      );
      return;
    }
    const res = await confirmCode(code, confirmId);
    if (res && res.token) {
      commit("loginTokenSuccess", res.token);
      dispatch("spinner/updateLoadingAction", false, { root: true });
      if (destination) {
        router.push(destination);
      }
    } else {
      dispatch("spinner/updateLoadingAction", false, { root: true });
      dispatch(
        "flashMessage/set",
        { type: "error", message: "Failed authenticate." },
        { root: true }
      );
    }
  },
  // login with email and password
  async loadAllLoginOptionsAction({ commit, rootState }) {
    const booking = rootState.booking.booking;

    if (
      !booking ||
      !booking.user_information ||
      !booking.user_information.bookingNumber
    ) {
      return commit("loginOptionsReceived", null);
    }
    const options = await getLoginOptions(
      booking.user_information.bookingNumber
    );
    options.push({
      text: "Select Preferred Contact",
      value: "",
      disabled: true,
    });
    commit("loginOptionsReceived", options.reverse());
  },
  // try to fetch data from cache and reinitilize state of user auth and booking if possible
  async initAuth({ dispatch, commit }, options) {
    const userData = persistData.getData("viewtrailData");
    const token = persistData.getData("token");
    if (token) {
      commit("setUserType", "corporate");
      commit("loginTokenSuccess", token);
    }
    if (userData && userData.name) {
      dispatch("spinner/updateLoadingAction", true, { root: true });
      const payload = {
        userData,
        noBookingInit: options.noBookingInit,
        openedBookingNumber: persistData.getData("openedBookingNumber"),
      };
      await dispatch("authorizedAction", payload);
      dispatch("spinner/updateLoadingAction", false, { root: true });
    } else {
      dispatch("spinner/updateLoadingAction", true, { root: true });
      const payload = {
        username: persistData.getData("openedBookingSurname"),
        bookingNumber: persistData.getData("openedBookingNumber"),
      };
      await dispatch("authorizedAction", payload);
      dispatch("spinner/updateLoadingAction", false, { root: true });
    }
  },
  async resetPasswordAction({ dispatch }, email) {
    dispatch("spinner/updateLoadingAction", true, { root: true });
    await resetPassword(email);
    dispatch(
      "flashMessage/set",
      { type: "success", message: "Link to your email has been sent." },
      { root: true }
    );
    dispatch("spinner/updateLoadingAction", false, { root: true });
  },
  async registerAction({ dispatch }, email) {
    dispatch("spinner/updateLoadingAction", true, { root: true });
    await resetPassword(email);
    dispatch(
      "flashMessage/set",
      { type: "success", message: "Link to your email has been sent." },
      { root: true }
    );
    dispatch("spinner/updateLoadingAction", false, { root: true });
  },
  resetStateAction({ commit }) {
    persistData.clearData("openedBookingNumber");
    persistData.clearData("viewtrailData");
    persistData.clearData("token");
    commit("resetState");
  },
  resetRememberMeAction() {
    persistData.clearData("viewtrailData");
  },
  logoutAction({ dispatch }, noRedirect) {
    dispatch("resetStores", null, { root: true });
    if (!noRedirect) {
      router.push("/login");
    }
  },
};

const mutations = {
  loginBookingNumberSuccess(state, data) {
    state.userData = data;
  },
  loginOptionsReceived(state, loginOptions) {
    state.loginOptions = loginOptions;
  },
  loginTokenSuccess(state, token) {
    state.token = token;
  },
  clearToken(state) {
    state.token = null;
  },
  confirmIdReceived(state, id) {
    state.confirmId = id;
  },
  setUserType(state, type) {
    state.type = type;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
};

export default {
  getters,
  namespaced: true,
  state,
  actions,
  mutations,
};
